import {dropDisabledNoRendererReads, foldCurveExtendsIntoMapping} from "#material/nodes/color/rgb-curve/validation"
import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {toStrictSchemaWithDynamicParameters} from "#material/nodes/base/material/schema"
import {color, versionChain} from "@cm/graph-v3"
import type {Vec2} from "#material/types/vector"
import {vec2} from "#material/types/vector"

export interface RgbCurveSockets extends MaterialNodeSockets {
    inputs: {
        color: "color" // found in inputs: color,float,normal,vector; found in settings: color; inferred from data: float3
        fac: "float" // found in inputs: color; found in settings: float,scalar; inferred from data: float
    }
    settings: {
        color: "color" // found in inputs: color,float,normal,vector; found in settings: color; inferred from data: float3
        colorMappingColorRampCyclesRampColorTable: "colorArray"
        fac: "float" // found in inputs: color; found in settings: float,scalar; inferred from data: float
        mappingBlackLevel: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        mappingClipMaxX: "float" // found in inputs: -; found in settings: float; inferred from data: float
        mappingClipMaxY: "float" // found in inputs: -; found in settings: float; inferred from data: float
        mappingClipMinX: "float" // found in inputs: -; found in settings: float; inferred from data: float
        mappingClipMinY: "float" // found in inputs: -; found in settings: float; inferred from data: float
        mappingCyclesMappingTable: "colorArray" // found in inputs: -; found in settings: vector_array; inferred from data: float3Array
        mappingExtend: "string" // found in inputs: -; found in settings: string; inferred from data: string
        mappingTone: "string" // found in inputs: -; found in settings: string; inferred from data: string
        mappingUseClip: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        mappingWhiteLevel: "color" // found in inputs: -; found in settings: color; inferred from data: float3
    }
    settingsExtensions: Partial<
        Record<`mappingCurves_${number}Points_${number}Location`, Vec2> & //mappingCurves_0Points_0Location: "point2" found in inputs: -; found in settings: vector; inferred from data: float2
            Record<`mappingCurves_${number}Points_${number}HandleType`, string> //mappingCurves_0Extend: "string" found in inputs: -; found in settings: string; inferred from data: string
    >
    outputs: {
        color: "color"
    }
}

export const rgbCurveDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeRGBCurve,
    },
    nodeClass: "RgbCurve",
    versionChain: versionChain([dropDisabledNoRendererReads, foldCurveExtendsIntoMapping]),
    ui: {
        color: "#8d802f",
        disableable: true,
        items: [
            {name: "color", valueType: "color", label: "Color", output: true},
            {custom: true, componentClass: "rgbCurve"},
            {name: "fac", valueType: "float", label: "Fac", defaultValue: 1, range: {min: 0, max: 1}, input: true, setting: true},
            {name: "color", valueType: "color", label: "Color", input: true, setting: true, defaultValue: {r: 0.0, g: 0.0, b: 0.0}},
            // {name: "mappingClipMaxY", valueType: "scalar", defaultValue: 1},
            // {name: "mappingClipMinX", valueType: "scalar", defaultValue: 0},
            // {name: "mappingClipMinY", valueType: "scalar", defaultValue: 0},
            // {name: "mappingExtend", valueType: "string", defaultValue: "EXTRAPOLATED"},
            // {name: "mappingTone", valueType: "string", defaultValue: "STANDARD"},
            // {name: "mappingUseClip", valueType: "boolean", defaultValue: true},
            // {name: "mappingWhiteLevel", valueType: "color", defaultValue: [1, 1, 1]},
        ],
        label: "RGB Curves",
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({color: optionalMaterialInput, fac: optionalMaterialInput}),
            settings: toStrictSchemaWithDynamicParameters(
                z.object({
                    // TODO: Do we need to include the mapping table below (generated by the blender addon)?
                    //https://github.com/colormass/production-tools/blob/00b669a79201fac6593adc55fa8927cab1859f71/blender/colormass_nodegraph.py#L102
                    color: z.optional(color),
                    colorMappingColorRampCyclesRampColorTable: z.optional(z.array(color)),
                    fac: z.optional(z.number()),
                    mappingBlackLevel: z.optional(color),
                    mappingClipMaxX: z.optional(z.number()),
                    mappingClipMaxY: z.optional(z.number()),
                    mappingClipMinX: z.optional(z.number()),
                    mappingClipMinY: z.optional(z.number()),
                    mappingCyclesMappingTable: z.optional(z.array(color)),
                    mappingExtend: z.optional(z.string()),
                    mappingTone: z.optional(z.string()),
                    mappingUseClip: z.optional(z.boolean()),
                    mappingWhiteLevel: z.optional(color),
                }),
                [
                    {
                        regex: /^mappingCurves_\d+Points_\d+Location$/,
                        schema: vec2,
                    },
                    {
                        regex: /^mappingCurves_\d+Points_\d+HandleType$/,
                        schema: z.string(),
                    },
                ],
            ),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<RgbCurveSockets>
